import * as Sentry from '@sentry/react';
import type ApolloClient from 'apollo-client';
import React from 'react';
import { animateScroll } from 'react-scroll';
import {
  isFunction,
  isPlainObject,
  get,
  setWith,
  updateWith,
  clone,
  hasIn,
  unset,
  defaults,
  merge,
  type PropertyPath,
} from 'lodash';
import type { Course, Organization, Product, User } from '../../App.interfaces';
import { Modal } from '../../components/Modal';
import {
  login,
  logout,
  getAuthState,
  getAppConfig,
  AppContext,
  authSignUp,
  authSignIn,
  initialize,
  loadClientAppConfig,
  getApiClient,
  hexToHslString,
  trackRegistrationEvent,
} from '../../helpers/api-helpers';
import pages from '../pages';
import { preloadRoute } from '../route-preload';
import { useHistory, useLocation } from 'react-router';
import useFeatureFlags from '@/hooks/use-feature-flags';
const SignUpConfirmationView = React.lazy(
  () => import('@/components/sign-up-confirmation-view'),
);

const maintenanceMessage = 'We are currently performing routine maintenance.';

export interface PreverificationData {
  firstName: string;
  lastName: string;
  username: string;
  email?: string;
  cognito?: { username: string };
  // The funnel the signup started in, captured here so the later
  // confirmation event is attributed to it rather than to whatever the
  // registration-revamp flag reads at confirm time.
  flow?: 'old' | 'new';
}

function getPreverificationData(): PreverificationData {
  const rawData = localStorage.getItem('__sgapp_preverify');
  return rawData && JSON.parse(rawData);
}

function setPreverificationData(data: PreverificationData) {
  localStorage.setItem('__sgapp_preverify', JSON.stringify(data));
}

function clearPreverificationData() {
  localStorage.removeItem('__sgapp_preverify');
}

export type Branding = {
  id: string | null;
  name: string;
  hostname: string | null;
  logoUrl: string | null;
  colorPrimary: string;
  colorSecondary: string;
};

// CSS custom properties that get overridden when a partner brand is active.
// Maps var name → which brand color to use. Single source of truth so apply
// and reset paths stay in sync.
// Asymmetry intentional: tailwind.config.js consumes --primary as `var(--primary)` (expects hex), but --secondary as `hsl(var(--secondary))` (expects HSL components).
const BRANDED_VAR_SOURCES: Record<
  string,
  'primaryHsl' | 'secondaryHsl' | 'primary' | 'secondary'
> = {
  '--primary': 'primary',
  '--secondary': 'secondaryHsl',
  '--dark-orange': 'primary',
  '--color-primary-500': 'primary',
  '--color-primary-600': 'primary',
  '--color-primary-700': 'primary',
  '--green-blue-accessible': 'secondary',
  '--color-secondary': 'secondary',
  '--color-persian-green-500': 'secondary',
  '--cta-button-color': 'secondary',
};

// Promise.allSettled for one promise: lets a caller attach the handler now and
// read the outcome later.
function settle<T>(promise: Promise<T>): Promise<PromiseSettledResult<T>> {
  return Promise.allSettled([promise]).then(([result]) => result);
}

// Release the paint-hold the index.html boot script starts on partner
// hostnames (no-op elsewhere).
function releaseBrandingPaintHold() {
  document.documentElement.classList.remove('branding-pending');
}

export type AppState = {
  apiClient: ApolloClient<unknown> | null;
  appConfig: Record<string, unknown>;
  authState: string;
  branding: Branding | null;
  error: unknown;
  isResetting: boolean;
  modals: React.ReactNode[];
  uiState: Record<string, unknown>;
  user: User | null;
};

export type AppContextType = Pick<
  AppState,
  'authState' | 'branding' | 'user'
> & {
  reset?: (autologin?: boolean, url?: string) => Promise<void>;
  signIn?: (
    username: string,
    password: string,
    options?: { preverify?: (data: PreverificationData) => Promise<unknown> },
  ) => Promise<void>;
  signUp?: (
    params: {
      username: string;
      password: string;
      firstName: string;
      lastName: string;
    },
    options?: { preverify?: (data: PreverificationData) => Promise<unknown> },
  ) => Promise<void>;
  getPreverificationData?: () => PreverificationData;
  getCourseDashboardUrl?: (course: Course | string) => string;
  featureFlags: Record<string, boolean>;
  isLoadingFeatureFlags: boolean;
  upsertFeatureFlag: (featureName: string, enabled: boolean) => Promise<void>;
};

export default function AppProvider({
  mainContentRef,
  children,
}: {
  mainContentRef: React.RefObject<HTMLElement | null>;
  children: (
    apiClient: AppState['apiClient'],
    error: unknown,
    userID?: string,
  ) => React.ReactNode;
}) {
  const [isFirstRender, setIsFirstRender] = React.useState(true);
  const [
    { apiClient, appConfig, authState, branding, error, user, uiState },
    setState,
  ] = React.useState<AppState>({
    apiClient: null,
    appConfig: {},
    authState: 'loading',
    branding: null,
    error: null,
    isResetting: false,
    modals: [],
    uiState: {},
    user: null,
  });
  const {
    featureFlags,
    isLoading: isLoadingFeatureFlags,
    upsertFeatureFlag,
  } = useFeatureFlags(user);
  const { pathname } = useLocation();
  const history = useHistory();
  // reset() is a stable callback, so it reads the current route from here.
  const pathnameRef = React.useRef(pathname);
  pathnameRef.current = pathname;

  const reset = React.useCallback(
    async (autologin = true, url?: string) => {
      setState((prevState) => ({
        ...prevState,
        error: null,
        isResetting: true,
      }));

      // Start downloading the route's code now, in parallel with the auth
      // requests below. Autologin is off only on /logout, which needs none.
      if (autologin) {
        preloadRoute(pathnameRef.current, { apiClient: null });
      }

      const w = window as unknown as {
        __handleDownForMaintenance: null | (() => void);
        __isHandlingDownForMaintenance: boolean;
      };
      let pendingLogin: Promise<PromiseSettledResult<unknown>> | undefined;
      try {
        w.__isHandlingDownForMaintenance = false;
        // A maintenance error can land while both requests below are still in
        // flight, before the real handler exists. Remember it, so a login that
        // hit maintenance behind a good config still shows the modal.
        let sawMaintenance = false;
        w.__handleDownForMaintenance = () => {
          sawMaintenance = true;
        };
        await initialize();
        // UserLogin needs only the api client, which initialize() has made, so
        // it goes out with the config request instead of after it. The config
        // is applied the moment it settles, even if login is still in flight,
        // and the login result is applied after it, as it always was. The login
        // promise gets its handler here so a failure that lands while the
        // config is still pending is never reported as unhandled.
        const loginRequest = autologin ? settle(login()) : undefined;
        const configResult = await settle(loadClientAppConfig());
        if (configResult.status === 'rejected') {
          throw configResult.reason;
        }
        const nextAppConfig = getAppConfig() as Record<string, unknown>;
        const nextBranding =
          (nextAppConfig.branding as Branding | undefined | null) ?? null;
        // Release the index.html paint-hold when there's no partner branding
        // to apply; the branding effect below releases it otherwise, after
        // setting the brand CSS variables.
        if (!nextBranding?.id) {
          releaseBrandingPaintHold();
        }
        setState((prevState) => ({
          ...prevState,
          authState: autologin ? 'loading' : 'signIn',
          appConfig: nextAppConfig,
          apiClient: getApiClient(),
          branding: nextBranding,
          uiState: {},
          user: null,
        }));
        const showMaintenanceModal = () => {
          if (w.__isHandlingDownForMaintenance) {
            return;
          }

          w.__isHandlingDownForMaintenance = true;
          Modal.push(
            <>
              <h3 className="modal-title">Down for Maintenance</h3>
              <div className="modal-alert-content">
                <p>{maintenanceMessage}</p>
              </div>
            </>,
            { closeOnBackgroundClick: false },
          );
        };
        w.__handleDownForMaintenance = showMaintenanceModal;
        if (sawMaintenance) {
          showMaintenanceModal();
        }
        pendingLogin = loginRequest;
      } catch (err) {
        const error = err as Error;
        console.error(error);
        let errorMsg =
          'Uh oh, we are experiencing an issue. Please try again later.';

        if (error.message.includes('DownForMaintenance')) {
          errorMsg = maintenanceMessage;
        }

        setState((prevState) => ({
          ...prevState,
          error: errorMsg,
          uiState: {},
          authState: 'error',
          user: null,
          isResetting: false,
        }));
        return;
      }

      if (autologin && pendingLogin) {
        try {
          const settledLogin = await pendingLogin;
          if (settledLogin.status === 'rejected') {
            throw settledLogin.reason;
          }
          const user = settledLogin.value as User | null;
          Sentry.setUser({ email: user?.email });
          if (user) {
            preloadRoute(pathnameRef.current, {
              apiClient: getApiClient(),
              userId: user.id,
            });
          }
          setState((prevState) => ({
            ...prevState,
            authState: getAuthState(),
            user,
          }));
        } catch (err) {
          console.error(err);
          const error = 'Login Error';
          setState((prevState) => ({
            ...prevState,
            error,
            authState: 'signIn',
            user: null,
            isResetting: false,
          }));
        }
      }

      if (url) {
        history.replace(url);
      }

      setState((prevState) => ({ ...prevState, isResetting: false }));
    },
    [history],
  );

  // No dependencies: only closes over module-level helpers and the stable
  // `Modal` singleton, never over component state.
  const confirmSignup = React.useCallback(
    async (email: string, onConfirmed?: () => Promise<void>) => {
      const modal = Modal.push(
        <SignUpConfirmationView
          email={email}
          onConfirmed={async () => {
            try {
              trackRegistrationEvent('signup_confirm_success', {
                flow: getPreverificationData()?.flow,
              });
              clearPreverificationData();
              await onConfirmed?.();
            } finally {
              modal.close();
            }
          }}
        />,
        { closeOnBackgroundClick: false },
      );

      // Wait for the modal to be closed before continuing
      await modal.wait();
    },
    [],
  );

  const signIn = React.useCallback(
    async (
      username: string,
      password: string,
      {
        preverify,
      }: { preverify?: (data: PreverificationData) => Promise<void> } = {},
    ) => {
      try {
        await authSignIn(username, password);
        clearPreverificationData();
        await reset();
      } catch (e) {
        const error = e as { code: string };
        if (error.code === 'UserNotConfirmedException') {
          if (preverify) {
            await preverify(getPreverificationData());
          }

          await confirmSignup(username, async () => {
            await authSignIn(username, password);
            await reset();
          });
          return;
        }
        throw error;
      }
    },
    [reset, confirmSignup],
  );

  const openProductCheckout = React.useCallback(
    (
      product: Product | string,
      queryParams:
        | string
        | string[][]
        | Record<string, string>
        | URLSearchParams
        | undefined,
    ) => {
      const searchQuery =
        queryParams &&
        URLSearchParams &&
        `?${new URLSearchParams(queryParams).toString()}`;
      history.push(
        `/subscribe/${(product as Product).id || product}${searchQuery || ''}`,
      );
    },
    [history],
  );

  const signUp = React.useCallback(
    async (
      params: {
        username: string;
        password: string;
        firstName: string;
        lastName: string;
      },
      {
        preverify,
      }: {
        preverify?: (params: PreverificationData) => Promise<void>;
      } = {},
    ) => {
      const authParams = {
        username: params.username,
        password: params.password,
        attributes: {
          email: params.username,
          given_name: params.firstName,
          family_name: params.lastName,
        },
      };

      const preverificationParams: PreverificationData = {
        username: params.username,
        firstName: params.firstName,
        lastName: params.lastName,
        flow: featureFlags['registration-revamp'] ? 'new' : 'old',
      };

      // Written only once Cognito accepts the sign-up: a failed retry (say, the
      // same email again from another tab) must not replace a record that
      // already holds the real id with one that has none.
      const signupResult = await authSignUp(authParams);
      setPreverificationData({
        ...preverificationParams,
        cognito: {
          username: signupResult.userSub,
        },
      });

      await signIn(authParams.username, authParams.password, { preverify });
    },
    [featureFlags, signIn],
  );

  // These five only ever close over the stable legacy `Modal` singleton --
  // pulled into their own zero-dependency callbacks (rather than defined
  // inline in the appContext memo below) so their identity stays stable
  // across *any* render, not just ones where the memo's other dependencies
  // also happen to be unchanged.
  const pushModal = React.useCallback(
    (modal: React.ReactNode | null) => Modal.push(modal),
    [],
  );
  const popModal = React.useCallback(() => Modal.pop(), []);
  const hideModal = React.useCallback(
    (modal: React.ReactNode) => Modal.close(modal),
    [],
  );
  const confirm = React.useCallback(
    (params: Record<string, unknown>) => Modal.confirm(params),
    [],
  );
  const alert = React.useCallback(
    (params: Record<string, unknown>) => Modal.alert(params),
    [],
  );

  React.useEffect(() => {
    if (isFirstRender) {
      const autologin = !/^\/logout\/?/.test(pathname);
      reset(autologin);
      setIsFirstRender(false);
    }
  }, [isFirstRender, pathname, reset]);

  React.useEffect(() => {
    const root = document.documentElement;
    const names = Object.keys(BRANDED_VAR_SOURCES);
    // Default brand (id === null) → clear any overrides so the stylesheet wins.
    if (!branding || branding.id === null) {
      names.forEach((name) => root.style.removeProperty(name));
      return;
    }
    try {
      const values = {
        primary: branding.colorPrimary,
        secondary: branding.colorSecondary,
        primaryHsl: hexToHslString(branding.colorPrimary),
        secondaryHsl: hexToHslString(branding.colorSecondary),
      };
      names.forEach((name) => {
        root.style.setProperty(name, values[BRANDED_VAR_SOURCES[name]]);
      });
    } catch {
      // Malformed color in the branding record — render the default theme
      // rather than crashing the app for this hostname.
      names.forEach((name) => root.style.removeProperty(name));
    }
    // Branding resolved either way — release the index.html paint-hold.
    releaseBrandingPaintHold();
  }, [branding]);

  React.useEffect(() => {
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    // Remember the defaults from index.html so we can restore them.
    if (icon && !icon.dataset.defaultHref) {
      icon.dataset.defaultHref = icon.href;
    }
    if (branding?.id) {
      document.title = `${branding.name || 'Recademics'} Study Guides`;
      if (icon && branding.logoUrl) {
        icon.href = branding.logoUrl;
      }
    } else {
      document.title = 'Recademics Study Guides';
      if (icon?.dataset.defaultHref) {
        icon.href = icon.dataset.defaultHref;
      }
    }
  }, [branding]);

  const appContext = React.useMemo(
    () => ({
      authState,
      branding,
      user,
      tabs: pages.filter(
        ({ showTab }) =>
          showTab === true ||
          (isFunction(showTab) && user && showTab({ user })),
      ),
      config: merge(
        appConfig,
        (window as unknown as { ReactAppEnv: unknown }).ReactAppEnv,
      ),
      signUp,
      getPreverificationData,
      clearPreverificationData,
      signIn,
      async signOut() {
        clearPreverificationData();
        Sentry.setUser(null);
        await logout();
        setState((prevState) => ({
          ...prevState,
          uiState: {},
          authState: getAuthState(),
          user: null,
        }));
      },
      reset,
      loadUIState(path: string | [string]) {
        return get(uiState, path);
      },
      saveUIState(path: PropertyPath, value: unknown, callback?: () => void) {
        setState((prevState) => {
          callback?.();
          const uiState = updateWith(
            clone(prevState.uiState),
            path,
            (oldValue) => {
              const newValue = isFunction(value) ? value(oldValue) : value;
              return !isPlainObject(oldValue)
                ? newValue
                : {
                    ...oldValue,
                    ...newValue,
                  };
            },
            clone,
          );
          return { ...prevState, uiState };
        });
      },
      wipeUIState(path: string | [string], callback?: () => void) {
        setState((prevState) => {
          callback?.();
          if (hasIn(prevState.uiState, path)) {
            // Create an immutable copy, with the path value cleared
            const uiState = setWith(
              clone(prevState.uiState),
              path,
              undefined,
              clone,
            );
            unset(uiState, path);
            return { ...prevState, uiState };
          }
          return prevState;
        });
      },
      // The screen can be dimmed without displaying a modal by passing in null
      pushModal,
      popModal,
      hideModal,
      onUpdateUser(updatedUser: User) {
        if (user && user.id === updatedUser.id) {
          setState((prevState) => ({
            ...prevState,
            user: {
              ...prevState.user, // Start with the previous values
              ...updatedUser, // Override with any new values from the update
              groups: prevState.user?.groups, // Groups come from auth token
            },
          }));
        }
      },
      confirm,
      alert,
      openCourseDashboard: (course: Course | string) => {
        history.push(`/courses/${(course as Course).id || course}/dashboard`);
      },
      openCourseCatalog: () => {
        history.push('/courses');
      },
      openProductCheckout,
      getCourseDashboardUrl: (course: Course | string) =>
        `/courses/${(course as Course).id || course}/dashboard`,
      getOrganizationDashboardUrl: (org: Organization | string) =>
        `/organizations/v3/${(org as Organization).id || org}`,
      scrollToTop: (options: unknown) => {
        animateScroll.scrollToTop(
          defaults(options, {
            duration: 250,
            container: mainContentRef.current,
          }),
        );
      },
      // `mainContentRef` itself never changes identity (it's a ref object passed
      // down once from the parent), so this is only re-read when the memo
      // recomputes for some other reason -- acceptable since the ref is attached
      // to a persistent root element that's set once, shortly after mount, and
      // AppProvider's own post-mount `reset()` call already forces a re-render
      // (and therefore a fresh read here) by the time anything downstream
      // observes it.
      dom: {
        mainContent: mainContentRef.current,
      },
      featureFlags,
      isLoadingFeatureFlags,
      upsertFeatureFlag,
    }),
    [
      authState,
      branding,
      user,
      appConfig,
      signUp,
      signIn,
      reset,
      uiState,
      history,
      openProductCheckout,
      pushModal,
      popModal,
      hideModal,
      confirm,
      alert,
      mainContentRef,
      featureFlags,
      isLoadingFeatureFlags,
      upsertFeatureFlag,
    ],
  );

  return (
    <AppContext.Provider value={appContext}>
      {children(apiClient, error, user?.id)}
    </AppContext.Provider>
  );
}
